import { useLayoutEffect, useRef } from 'react';
import { AnimatePresence, motion, useAnimate } from 'motion/react';
import type { RefObject } from 'react';
import { CountdownTimer } from '../CountdownTimer';
import { watchUrlForPhase } from '../../content/site';
import type { EventStateView } from '../../lib/eventState';
import { useMotionEnabled } from '../../lib/motion';
import { plausibleEventClass, trackEvent } from '../../lib/plausible';

/**
 * Docked state bar (2026-07-17): appears once the hero's state element
 * (countdown / LIVE NOW / WATCH) scrolls past the top of the page, and
 * leaves when it re-enters. Black to match the theme, with a slight red
 * drop shadow on the bottom edge. Logo (left, doubles as back-to-top) +
 * the state element centered; the top-right corner is reserved for a
 * future hamburger menu.
 *
 * Entrance choreography: the bar slides down while the state element runs
 * a measured FLIP — it starts at the hero state element's current rect
 * (just off the top edge, at hero scale) and settles into the bar, so the
 * hero text reads as translating into place. Measuring at reveal time
 * keeps it correct at any resolution; when the anchor can't be measured
 * it falls back to a generic drop-in. Interactivity mirrors the hero:
 * countdown and LIVE NOW are display-only, WATCH links the admin watch URL when
 * set. Reduced motion renders the bar statically (instant show/hide).
 *
 * Visual duplicates of hero content are aria-hidden so screen readers
 * don't hear the page state twice; the logo button and watch link stay
 * accessible.
 */

const BAR_SPRING = { type: 'spring', stiffness: 260, damping: 30 } as const;

/** z-40: above all page content, below the skip link's focus:z-50. */
const SHELL_CLASS =
  'fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-center bg-black ' +
  'shadow-[0_3px_16px_rgba(239,64,91,0.35)] sm:h-16';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function LogoButton() {
  const motionOn = useMotionEnabled();
  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={() => {
        trackEvent('UI Click', { target: 'appbar-logo' });
        window.scrollTo({ top: 0, behavior: motionOn ? 'smooth' : 'auto' });
      }}
      // Hidden below 380px: guarantees the logo can never crowd the state
      // text on ultra-narrow viewports.
      className="absolute left-4 top-1/2 hidden -translate-y-1/2 min-[380px]:block sm:left-6"
    >
      <img src="/assets/images/logos/hyperx.svg" alt="" className="w-[92px] sm:w-[118px]" />
    </button>
  );
}

function BarState({ event }: { event: EventStateView | null }) {
  const phase = event?.phase ?? 'PRE';
  const headingClass =
    'text-glow font-display uppercase leading-none text-brand-white ' +
    '[font-size:clamp(1.3rem,3vw,2.6rem)]';

  if (phase === 'PRE') {
    if (!event) return null;
    return (
      <div aria-hidden="true">
        <CountdownTimer
          showStartAt={event.showStartAt}
          serverOffsetMs={event.serverOffsetMs}
          sizeClass="[font-size:clamp(1.15rem,2.6vw,2.2rem)]"
          glowClass="[filter:drop-shadow(0_0_6px_rgba(255,255,255,0.55))]"
        />
      </div>
    );
  }
  if (phase === 'LIVE') {
    return (
      <p aria-hidden="true" className={headingClass}>
        LIVE NOW
      </p>
    );
  }
  if (!event) return null;
  // The bar's watch link is a second, independent path to the same destination
  // as the hero's — tagged with its own `location` so the two don't get
  // conflated (this one only exists once the visitor has scrolled). Mirrors the
  // hero exactly: the word "WATCH", always linked (client 2026-08-17).
  return (
    <a
      href={watchUrlForPhase(phase, event.watchUrl)}
      target="_blank"
      rel="noreferrer"
      aria-label="Watch (opens in a new tab)"
      className={`${headingClass} ${plausibleEventClass('CTA Click', { location: 'appbar-replay' })}`}
    >
      WATCH
    </a>
  );
}

function AnimatedBar({
  event,
  heroAnchor,
}: {
  event: EventStateView | null;
  heroAnchor: RefObject<HTMLDivElement>;
}) {
  const [scope, animate] = useAnimate();
  const stateRef = useRef<HTMLDivElement>(null);

  // Pre-paint (useLayoutEffect): measure and launch bar slide + state FLIP
  // so the first painted frame is already the animation's first frame.
  useLayoutEffect(() => {
    const bar = scope.current as HTMLElement | null;
    const state = stateRef.current;
    if (!bar || !state) return;
    animate(bar, { y: ['-100%', '0%'] }, BAR_SPRING);

    const barH = bar.getBoundingClientRect().height;
    // Generic drop-in fallback.
    let startY = -barH * 0.6;
    let startScale = 1.35;
    const hero = heroAnchor.current?.getBoundingClientRect();
    const rest = state.getBoundingClientRect();
    if (hero && hero.height > 0 && rest.height > 0) {
      // The bar itself starts a full barH higher — add it back so the
      // state's on-screen start matches the hero rect it just left.
      startY = clamp(hero.top + hero.height / 2 - (rest.top + rest.height / 2) + barH, -280, 0);
      startScale = clamp(hero.height / rest.height, 1, 3);
    }
    animate(state, { y: [startY, 0], scale: [startScale, 1], opacity: [0, 1] }, BAR_SPRING);
  }, [scope, animate, heroAnchor]);

  return (
    <motion.header
      ref={scope}
      initial={false}
      exit={{ y: '-100%' }}
      transition={BAR_SPRING}
      className={SHELL_CLASS}
    >
      <LogoButton />
      <motion.div ref={stateRef} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
        <BarState event={event} />
      </motion.div>
    </motion.header>
  );
}

type AppBarProps = {
  event: EventStateView | null;
  visible: boolean;
  /** The hero's state element — measured at reveal for the FLIP start. */
  heroAnchor: RefObject<HTMLDivElement>;
};

export function AppBar({ event, visible, heroAnchor }: AppBarProps) {
  const motionOn = useMotionEnabled();

  if (!motionOn) {
    // Static bar: same shell, instant show/hide, zero transforms.
    return visible ? (
      <header className={SHELL_CLASS}>
        <LogoButton />
        <BarState event={event} />
      </header>
    ) : null;
  }

  return (
    <AnimatePresence>
      {visible && <AnimatedBar key="appbar" event={event} heroAnchor={heroAnchor} />}
    </AnimatePresence>
  );
}
